import { faPenToSquare } from "@fortawesome/pro-solid-svg-icons"
import { Box, Button, Grid, List, Tabs, Typography, useMediaQuery, useTheme } from "@mui/material"
import { IApiFunc, IApiResult } from "auth/interface/api.interface"
import { useApi } from "auth/useApi3"
import DefaultButton from "components/Button"
import FormComponents from "components/Form/FormComponents/FormComponents"
import FormComponentsView from "components/Form/FormComponents/FormComponentsView"
import { IFormFieldProps } from "components/Form/FormField/formfield.interface"
import { getValidation } from "components/Form/FormTemplate/validation"
import PaperBox from "components/PaperBox"
import { Formik } from "formik"
import { apiEndpoints } from "generated/apiEndpoints"
import { allRoutes } from "generated/routes"
import React, { useEffect, useState } from "react"
import { Link } from "react-router-dom"
import { StyledCustomFieldsList, StyledFontAwesomeIconEdit, StyledIconButtonEdit } from "shared/vehicle/CustomFields/CustomFields.styled"
import * as Yup from "yup"
import { removeBrackets, removeDataField } from "helpers/CustomField/file"
import {
  CategoryTabsProps,
  UserCustomFieldDto,
  UserCustomFieldFormFieldDto,
  UserCustomFieldValuesProps,
  UserCustomFieldWithCategory
} from "./UserCustomField.interfaces"
import { StyledGridBorderGrey, StyledTabVertical } from "./UserCustomField.styled"
import { useTranslation } from "react-i18next"

const CategoryTabs = ({ categoryTabValue, setCategoryTabValue, categories, formikProps, cachedValues }: CategoryTabsProps) => {
  const { errors, touched } = formikProps
  const theme = useTheme()
  const isPhone = useMediaQuery(theme.breakpoints.down("sm"))

  const countCategoryErrors = (category: string) => {
    return Object.keys(errors).filter(key => cachedValues[key]?.category === category && touched[key]).length
  }

  return (
    <StyledGridBorderGrey>
      <Tabs
        sx={{
          "& .MuiButtonBase-root": {
            padding: "16px 30px",
            [theme.breakpoints.down("sm")]: {
              padding: "16px 20px",
            },
          },
          "& .MuiTabs-scroller": {
            marginTop: isPhone ? "0" : "40px",
          }
        }}
        indicatorColor="primary"
        value={categoryTabValue}
        onChange={(_, newValue) => {
          setCategoryTabValue(newValue)
        }}
        variant="scrollable"
        TabIndicatorProps={{ style: { left: !isPhone ? 0 : 'auto' } }}
        orientation={isPhone ? "horizontal" : "vertical"}
      >
        {categories.map((category, index) => {
          const errorCount = countCategoryErrors(category)

          return (
            <StyledTabVertical
              id={`tab-${index}`}
              label={errorCount ? category + ` (${errorCount})` : category}
              key={index}
              tabIndex={index}
              tabValue={categoryTabValue}
            />
          )
        })}
      </Tabs>
    </StyledGridBorderGrey>
  )
}

export const UserCustomFieldValues = ({ userResult }: UserCustomFieldValuesProps) => {
  const [userCustomFieldValuesResult, userCustomFieldValuesApi] = useApi() as [IApiResult, IApiFunc]
  const [userCustomFieldUpsertResult, userCustomFieldValuesUpsertApi] = useApi() as [IApiResult, IApiFunc]
  const [categoryTabValue, setCategoryTabValue] = useState(0)
  const [editMode, setEditMode] = useState(false)
  const [userCustomFieldValidation, setUserCustomFieldValidation] = useState<any>()
  const [formikValues, setFormikValues] = useState<{ [x: number | string]: string }>({})
  const [cachedValueFields, setCachedValueFields] = useState<{ [key: number | string]: UserCustomFieldWithCategory }>({})
  const [categories, setCategories] = useState<string[]>([])
  const { t } = useTranslation()
  const theme = useTheme()
  const isPhone = useMediaQuery(theme.breakpoints.down("sm"))

  useEffect(() => {
    if (userResult.status !== 2) return
    fetchUserCustomFieldValues()
  }, [userResult])

  useEffect(() => {
    if (userCustomFieldValuesResult.status !== 2) return
    const initialFormikValues: { [p: number | string]: string } = {}
    const cachedFormikValues: { [p: number | string]: UserCustomFieldWithCategory } = {}
    const validationShape: { [p: number | string]: Yup.Schema } = {}
    const categoriesSet = new Set<string>()
    const firstInCategory: { [category: string]: boolean } = {}

    userCustomFieldValuesResult.data.forEach((userCustomField: UserCustomFieldDto, parentIndex: number) => {
      categoriesSet.add(userCustomField.category.name)
      userCustomField.valueFields.forEach((valueField: UserCustomFieldFormFieldDto, index: number) => {
        const key = valueField.userCustomFieldValueId > 0 ? valueField.userCustomFieldValueId : `userCustomFieldValueId_${parentIndex}_${index}`
        const userCustomFieldWithCategory: UserCustomFieldWithCategory = {
          ...valueField,
          formikKey: key,
          category: userCustomField.category.name,
          isFirstInCategory: !firstInCategory[userCustomField.category.name]
        }
        firstInCategory[userCustomField.category.name] = true
        if (index === 0) {
          userCustomFieldWithCategory.parentName = userCustomField.name
        }
        initialFormikValues[key] = valueField.value
        cachedFormikValues[key] = userCustomFieldWithCategory
        validationShape[key] = getValidation(valueField.dataType, !valueField.isRequired, valueField.name, t, valueField.labelKey)
      })
    })

    setFormikValues(initialFormikValues)
    setCategories(Array.from(categoriesSet))
    setUserCustomFieldValidation(Yup.object().shape(validationShape))
    setCachedValueFields(cachedFormikValues)
  }, [userCustomFieldValuesResult])

  useEffect(() => {
    if (userCustomFieldUpsertResult.status !== 2) return
    fetchUserCustomFieldValues()
    setEditMode(false)
  }, [userCustomFieldUpsertResult])

  const handleFormikSubmit = (formikValues: any) => {
    const formData = Object.entries(formikValues).map(([key, value]) => {
      const original = cachedValueFields[key]
      const isFile = original.component === "Upload"
      let formikValue
      if (value === null || value === "") {
        formikValue = null
      } else {
        const stringValue = value as string
        formikValue = typeof value === "number" ? value.toString() : value
        if (isFile) {
          formikValue = removeBrackets(stringValue)
          formikValue = JSON.stringify(removeDataField(JSON.parse(formikValue)))
        }
      }
      const id: number = isNaN(parseInt(key)) ? 0 : parseInt(key)
      return {
        id: id,
        userCustomFieldValueFieldId: original.userCustomFieldValueFieldId,
        userId: userResult.data.id,
        value: formikValue
      }
    })

    const url = apiEndpoints.usercustomfieldsadmin.upsertusercustomfieldvalues
    userCustomFieldValuesUpsertApi.put(url, formData)
  }

  function fetchUserCustomFieldValues() {
    const url = apiEndpoints.usercustomfieldsadmin.getusercustomfieldformvalues.replace("{userId}", userResult.data.id)
    userCustomFieldValuesApi.get(url)
  }

  return (
    <PaperBox mt={0}>
      {userCustomFieldValuesResult.status === 2 && userCustomFieldValuesResult.data.length === 0 ? (
        <Box p={3}>
          <Typography variant="bodyLarge" mb={3}>
            {t("settings.user.there_is_no_values_to_display", "There are no values ​​to display. Click the button to navigate to Custom Fields")}
          </Typography>
          <Button
            component={Link}
            to={allRoutes.CustomerSettingsUsersCustomfields()}
            color="primary"
            variant="contained"
          >
            {t("settings.user.go_to_custom_fields", "Go to Custom Fields")}
          </Button>
        </Box>
      ) : (
        <Formik
          validationSchema={userCustomFieldValidation}
          initialValues={formikValues}
          onSubmit={handleFormikSubmit}
          enableReinitialize={true}
        >
          {(props) => (
            <form id="UserCustomFields" onSubmit={props.handleSubmit} noValidate style={{ position: "relative" }}>
              <StyledIconButtonEdit
                top={isPhone ? 66 : 16}
                right={16}
                size="medium"
                color="primary"
                onClick={() => {
                  setEditMode(!editMode)
                }}>
                <StyledFontAwesomeIconEdit
                  editMode={editMode}
                  icon={faPenToSquare}
                  size="lg"
                />
              </StyledIconButtonEdit>
              <Grid container>
                <Grid item xs={12} sm={4} lg={2}>
                  <CategoryTabs
                    categoryTabValue={categoryTabValue}
                    setCategoryTabValue={setCategoryTabValue}
                    categories={categories}
                    formikProps={props}
                    cachedValues={cachedValueFields}
                  />
                </Grid>
                <Grid item xs={12} sm={8} lg={6} xl={4} px={{ xs: 2, sm: 5 }} pt={5} pb={{ xs: 2, sm: 5 }}>
                  <Grid container>
                    <Grid item xs={12}>
                      <StyledCustomFieldsList>
                        {Object.entries(cachedValueFields)
                          .filter(([_, valueField]) => (valueField.category === categories[categoryTabValue]))
                          .map(([_, valueField]) => {
                            const isFile = valueField.component === "Upload" && valueField.value !== null && valueField.value !== ""
                            const field: IFormFieldProps = {
                              component: valueField.component,
                              dataType: valueField.dataType,
                              formFieldKey: "",
                              id: valueField.formikKey,
                              label: "",
                              labelDefault: valueField.name,
                              optional: !valueField.isRequired,
                              labelKey: valueField.userCustomFieldValueId.toString(),
                              value: (isFile && !editMode) ? `[${valueField.value}]` : valueField.value,
                              key: valueField.formikKey.toString()
                            }
                            return <>
                              {valueField.parentName && (
                                <Typography variant="bodyMedium" mt={valueField.isFirstInCategory ? 0 : 4} fontWeight={700}>
                                  {valueField.parentName}
                                </Typography>
                              )}
                              {editMode ? (
                                <FormComponents
                                  field={field}
                                  initialValues={formikValues}
                                  handleNext={() => { }}
                                  formikProps={props}
                                  feature="usercustomfield"
                                />
                              ) : (
                                <Box>
                                  <FormComponentsView field={field} />
                                </Box>
                              )}
                            </>
                          })
                        }
                      </StyledCustomFieldsList>
                    </Grid>
                  </Grid>
                  {editMode && (
                    <Grid container justifyContent="flex-end" mt={4}>
                      <Box display="flex" gap={3}>
                        <Button
                          color="primary"
                          variant="outlined"
                          sx={{ width: "150px" }}
                          onClick={() => {
                            setEditMode(false)
                          }}
                        >
                          {t("global.cancel", "Cancel")}
                        </Button>
                        <DefaultButton
                          color="primary"
                          variant="contained"
                          type="submit"
                          disabled={!props.dirty}
                          sx={{ width: "150px" }}
                          result={userCustomFieldUpsertResult}
                        >
                          {t("global.save", "Save")}
                        </DefaultButton>
                      </Box>
                    </Grid>
                  )}
                </Grid>
              </Grid>

            </form>
          )}
        </Formik>
      )
      }
    </PaperBox >
  )
}
